import { useNavigate } from "react-router-dom";
import { Paper, Center, Loader, Text, ScrollArea, useMantineTheme, Title, Group, Progress, Stack, RingProgress, ThemeIcon, Button } from "@mantine/core";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";

import { useGetWorkOrdersAssignedToMeQuery, useGetWorkOrdersRequestedByMeQuery } from "../../api/workorder.api";
import { ListWorkOrderDto, WorkOrderStatus } from "../../models/workorder.models";
import { WorkOrderListItem } from "../WorkOrders/WorkOrderListItem";
import { IconCircleXFilled, IconClipboardList } from "@tabler/icons-react";

interface RequestedByMeWorkOrderListProps {
	selectedWorkOrderId?: string | null;
}

export const RequestedByMeWorkOrderList = ({ selectedWorkOrderId }: RequestedByMeWorkOrderListProps) => {
	const theme = useMantineTheme();
	const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.xs})`, true);
	const navigate = useNavigate();

	const { data: workOrders, isLoading, isError } = useGetWorkOrdersRequestedByMeQuery();
	const [showCompleted, { toggle: toggleShowCompleted }] = useDisclosure(false);

	const sortedWorkOrders = workOrders?.sort((a, b) => new Date(b.Created).getTime() - new Date(a.Created).getTime()) || [];
	const openWorkOrders = sortedWorkOrders.filter(wo => wo.Status != WorkOrderStatus.Completed);
	const anyCompletedWorkOrders = sortedWorkOrders.filter(wo => wo.Status == WorkOrderStatus.Completed).length > 0;
	const displayedWorkOrders = showCompleted ? sortedWorkOrders : sortedWorkOrders.filter(wo => wo.Status != WorkOrderStatus.Completed);

	const handleWorkOrderSelected = (workOrder: ListWorkOrderDto): void => {
		navigate(`/workorders/${workOrder.WorkOrderId}`);
	}

	return <>
		<Group mb="xs" justify="space-between" align="start" wrap={isMobile ? "wrap" : "nowrap"} gap={0}>
			<Title order={4}>Work Orders Requested by You</Title>
			{!isLoading && sortedWorkOrders.length > 0 && <>
				<Group gap={4} wrap="nowrap" mb="-10px">
					<Stack gap={0} align="end" visibleFrom="sm">
						<Text size="xs" c="dimmed">Completed</Text>
						<Text size="xs" c="dimmed">{sortedWorkOrders.length - openWorkOrders.length} of {sortedWorkOrders.length}</Text>
					</Stack>
					<RingProgress
						roundCaps
						thickness={3}
						size={30}
						sections={[{ value: ((sortedWorkOrders.length - openWorkOrders.length) / sortedWorkOrders.length) * 100, color: 'green' }]}
					/>
				</Group>
			</>}
		</Group>
		<Paper withBorder p={0} radius="md" mb="md" style={{ overflow: 'hidden' }}>
			<ScrollArea type={isMobile ? "never" : "hover"} style={{ height: isMobile ? 'unset' : 'calc(100vh - 195px)', minHeight: isMobile ? 'calc(100vh - 200px)' : 'calc(100vh - 195px)' }}>
				{displayedWorkOrders.map(workOrder => (
					<WorkOrderListItem
						key={workOrder.WorkOrderId}
						workOrder={workOrder}
						selected={selectedWorkOrderId === workOrder.WorkOrderId}
						onSelect={handleWorkOrderSelected}
						showLocation={true}
					/>
				))}
				{workOrders?.length === 0 && <>
					<Stack align="center" justify="center" mt="100px" px="sm">
						<ThemeIcon size={100} radius={100} opacity={.5} variant="light" color="gray">
							<IconClipboardList size={60} />
						</ThemeIcon>
						<Text style={{ textAlign: 'center' }} lineClamp={1} c="dimmed">No work orders requested by you</Text>
					</Stack>
				</>}
				{!isLoading && anyCompletedWorkOrders &&
				<Stack p="sm" align="center">
					<Button variant="light" radius="xl" w="fit-content" onClick={toggleShowCompleted}>{showCompleted ? "Hide" : "Show"} Completed</Button>
				</Stack>
				}
				{isLoading && <>
					<Center>
						<Loader size="sm" mt="lg" />
					</Center>
				</>}
				{isError && <>
					<Stack align="center" justify="center" mt="100px" px="sm" gap="0">
						<ThemeIcon size={60} radius={100} variant="transparent" color="red">
							<IconCircleXFilled size={60} />
						</ThemeIcon>
						<Text style={{ textAlign: 'center' }} lineClamp={1} c="dimmed">Unable to load work orders at this time</Text>
					</Stack>
				</>}
			</ScrollArea>
		</Paper>
	</>;
};